import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useTranslation } from 'react-i18next';

import leadActions from '../../Actions/leadActions';
import commonActions from '../../Actions/commonActions';
import routes from '../../Constants/routes';
import PanelActions from '../../Components/PanelActions';
import LeadForm from '../../Components/Leads/LeadForm';
import ActionHistory from '../../Components/ActionHistory';
import RelatedLeads from '../../Components/Leads/RelatedLeads';
import SendEmailModal from '../../Components/Leads/SendEmailModal';
import AppointmentModal from '../../Components/Leads/AppointmentModal';
import CloseModal from '../../Components/Leads/CloseModal';
import BuyModal from '../../Components/Leads/BuyModal';
import PhoneLink from '../../Components/PhoneLink';
import LeadCustomers from '../../Components/Leads/LeadCustomers';
import customerRoutes from "../../Constants/Customer/customerRoutes";
import './Lead.css';
import Card from "../../Components/Card/Card";
import { Dropdown } from "../../Components/FormComponents/Dropdown";

function LeadDetailsContainer ({ navigate, lead, moveToDetails }) {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const leadActionsBound = bindActionCreators(leadActions, dispatch);
    const commonActionsBound = bindActionCreators(commonActions, dispatch);

    const [showSendEmailModal, setShowSendEmailModal] = useState(false);
    const [showAppointmentModal, setShowAppointmentModal] = useState(false);
    const [showCloseModal, setShowCloseModal] = useState(false);
    const [showBuyModal, setShowBuyModal] = useState(false);
    const [emailActionLogId, setEmailActionLogId] = useState(0);

    useEffect(() => {
        return () => {
            commonActionsBound.clearStoreSelected();
        };
    }, []);

    function onNoAnswerClick() {
        if (!lead) return;
        const payload = {data: {action: "No answer"}};
        leadActionsBound.saveAction(lead.id, payload, false);
        refresh();
    }

    function onBusyClick() {
        if (!lead) return;
        const payload = {data: {action: "Busy"}};
        leadActionsBound.saveAction(lead.id, payload, false);
        refresh();
    }

    function onCloseClick(modalName) {
        if (modalName) toggleModal(modalName);
        refresh();
    }

    function onReopenClick() {
        if (!lead) return;
        leadActionsBound.reopen(lead.id);
        leadActionsBound.getMyLeads();
    }

    function onUnassignClick() {
        if (!lead) return;
        const updatedLead = {...lead, assignee: ""};
        leadActionsBound.unassignLead(updatedLead);
        if (navigate) navigate(routes.leads);
    }

    function toggleModal(modalName, emailLogId = 0) {
        switch (modalName) {
            case 'showSendEmailModal':
                setShowSendEmailModal(v => !v);
                setEmailActionLogId(emailLogId);
                break;
            case 'showAppointmentModal':
                setShowAppointmentModal(v => !v);
                setEmailActionLogId(emailLogId);
                break;
            case 'showCloseModal':
                setShowCloseModal(v => !v);
                setEmailActionLogId(emailLogId);
                break;
            case 'showBuyModal':
                setShowBuyModal(v => !v);
                setEmailActionLogId(emailLogId);
                break;
            default:
        }
    }

    function refresh() {
        if (!lead) return;
        leadActionsBound.getLead(lead.id, true, true);
        leadActionsBound.getMyLeads();
    }

    function onAssingButtonClick() {
        if (!lead) return;
        leadActionsBound.save(lead, refresh);
    }

    function onBuyClick(actionName) {
        switch (actionName) {
            case "order":
                if (navigate) navigate(customerRoutes.customer(lead.customerNumber, customerRoutes.orderPlacement));
                break;
            case "manualOrder":
                toggleModal("showBuyModal");
                break;
            case "closeLead":
                toggleModal("showCloseModal");
                break;
            case "appointment":
                toggleModal("showAppointmentModal");
                break;
            case "sendEmail":
                toggleModal("showSendEmailModal");
                break;
            case "noAnswer":
                onNoAnswerClick();
                break;
            case "reopenLead":
                onReopenClick();
                break;
            case "unassignLead":
                onUnassignClick();
                break;
            case "busy":
                onBusyClick();
                break;
            default:
                return null;
        }
    }

    function sortListByKey(listName, list) {
        return key => {
            commonActionsBound.sortListByKey(list, key, listName);
        }
    }

    function onCloseDetails() {
        leadActionsBound.clearLead();
        if (navigate) navigate(routes.leads);
    }

    if (!lead) return null;

    const detailsWidth = 12;
    const rightContainerWidth = 12;

    let actionDropdownOptions = [];

    if (lead.assignee) {
        actionDropdownOptions.push({title: t('leaddetailscontainer.unassignlead'), identifier: "unassignLead"});
    }

    let additionalDropdownOptions = [
        {title: t('appointmentdetailactions.busy'), identifier: "busy"},
        {title: t('leaddetailscontainer.noanswer'), identifier: "noAnswer"},
        {title: t('leaddetailscontainer.sendemail'), identifier: "sendEmail"},
        {title: t('leaddetailscontainer.createappointment'), identifier: "appointment"},
        {title: t('leaddetailscontainer.closelead'), identifier: "closeLead"},
    ];

    actionDropdownOptions = actionDropdownOptions.concat(additionalDropdownOptions);

    const placeOrderOptions = [
        {title: t('leaddetailscontainer.placeorder'), identifier: "order"},
        {title: t('leaddetailscontainer.manualorder'), identifier: "manualOrder"}
    ];

    if (lead.customerNumber !== null)
        actionDropdownOptions.unshift(...placeOrderOptions);

    const actionDropdownOptionsWhenLeadIsClosed = [
        {title: t('leaddetailscontainer.reopen'), identifier: 'reopenLead'}
    ];

    if (lead.isClosed)
        actionDropdownOptions = actionDropdownOptionsWhenLeadIsClosed;

    return (
        <div className="row lead-panel">
            {showSendEmailModal && (
                <SendEmailModal leadType="lead" lead={lead} onClose={() => toggleModal("showSendEmailModal")}
                                onSave={() => onCloseClick("showSendEmailModal")} emailActionLogId={emailActionLogId}/>
            )}
            {showAppointmentModal && (
                <AppointmentModal leadType="lead" lead={lead}
                                  onClose={() => toggleModal("showAppointmentModal")}
                                  onComplete={() => onCloseClick()}/>
            )}
            {showCloseModal && (
                <CloseModal lead={lead} onClose={() => toggleModal("showCloseModal")}
                            onSave={() => onCloseClick()}/>
            )}
            {showBuyModal && (
                <BuyModal lead={lead} onClose={() => toggleModal("showBuyModal")}
                          onSave={() => onCloseClick()}/>
            )}

            <div className={`col-${detailsWidth} p-0`}>
                <Card>
                    <Card.Header>
                        <Card.Header.Title>{!lead.isClosed ? (
                            <PanelActions editLink={routes.leadEdit(lead.id)} onRefresh={refresh}/>
                        ) : <>{t('leaddetailscontainer.leaddetails')}</>}</Card.Header.Title>
                        <Card.Header.Buttons>
                            <div className="btn-group">
                                <Dropdown
                                    placeholder={t('leaddetailscontainer.actions')}
                                    disabled={false}
                                    options={actionDropdownOptions}
                                    onChange={onBuyClick}
                                    valueProperty={"identifier"}
                                    property={"title"}
                                >
                                    {t('leaddetailscontainer.actions')}
                                </Dropdown>
                                {!lead.assignee && !lead.isClosed && (
                                    <button className="btn btn-sm" onClick={onAssingButtonClick}>
                                        <span className="vismaicon vismaicon-sm vismaicon-user"></span>
                                        {t('leaddetailscontainer.assigntome')}
                                    </button>
                                )}
                                {!lead.isClosed && (
                                    <PhoneLink
                                        phoneNumber={lead.phoneNumber}
                                        button={true}
                                        label={t('leaddetailscontainer.callcustomer')}
                                        icon={true}
                                    />
                                )}
                            </div>
                        </Card.Header.Buttons>
                    </Card.Header>
                    <Card.Content>
                        <LeadForm
                            lead={lead}
                            onFieldChange={() => {
                            }}
                            readonly={true}
                            onCancelClick={() => onCloseClick()}
                            onReopenClick={onReopenClick}
                            onAssingButtonClick={onAssingButtonClick}
                            onCloseClick={onCloseDetails}
                        />
                    </Card.Content>
                </Card>
                <br/>
            </div>
            <div className="col-12 p-0">
                <RelatedLeads
                    leads={lead.leadsForCustomerEmail}
                    moveToDetails={(leadId) => moveToDetails(leadId)}
                />
                <br/>
            </div>

            <div className={`col-${rightContainerWidth} p-0`}>
                <div className="row">
                    <div className="col-6 p-0">
                        <ActionHistory
                            actionHistory={lead.actionHistory}
                            toggleModal={(modalId, emailActionLogId) => toggleModal(modalId, emailActionLogId)}
                        />
                    </div>
                    <div className="col-6 p-0">
                        <LeadCustomers lead={lead}/>
                    </div>
                </div>
                {/* TODO add customerproducts here */}
            </div>
        </div>
    );
}

export default LeadDetailsContainer;